import React from 'react';
import { renderSafeHtml } from '../../../utils/renderHtml';
import { Tooltip } from 'react-tooltip';
import { Info } from 'lucide-react';
import MuiTooltip from '@mui/material/Tooltip';
import { DISPLAY_NAME_SOURCE } from '../../../services/api/domains/products/types';

interface SynonymsRadioFieldProps {
  label: string;
  preferredValue: string;
  synonyms: string[];
  onChange: (value: string | null) => void;
  isDisabled: boolean;
  tooltipText?: string;
  styles: Record<string, React.CSSProperties>;
  onFocus?: () => void;
  onBlur?: () => void;
  hasValue?: boolean;
  isFocused?: boolean;
  getFloatingLabelStyle?: (
    fieldName: string,
    hasValue: boolean
  ) => React.CSSProperties;
  getFloatingInputStyle?: (
    isTextarea: boolean,
    fieldName: string
  ) => React.CSSProperties;
  /** Pre-resolved display name from the detail endpoint (VIEW mode primary display) */
  displayName?: string;
  /** Source of the resolved display name — drives the source label */
  displayNameSource?: string | null;
  /**
   * When true, render only the synonyms list (no text input or display-name block).
   * Used by the separate `synonyms_list` field that sits below ob_product_name.
   */
  synonymsOnly?: boolean;
}

const SynonymsRadioField: React.FC<SynonymsRadioFieldProps> = ({
  label,
  preferredValue,
  synonyms,
  onChange,
  isDisabled,
  tooltipText,
  styles,
  onFocus,
  onBlur,
  hasValue = false,
  isFocused = false,
  getFloatingLabelStyle,
  getFloatingInputStyle,
  displayName,
  displayNameSource,
  synonymsOnly = false,
}) => {
  // Sort synonyms alphabetically
  const sortedSynonyms = [...(synonyms || [])].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );

  const handlePreferredNameChange = (value: string) => {
    onChange(value || null);
  };

  const handleRadioClick = (synonym: string) => {
    if (isDisabled) return;

    // If clicking the already-selected radio, uncheck it
    if (preferredValue === synonym) {
      onChange(null);
    } else {
      onChange(synonym);
    }
  };

  // Show placeholder only when focused and empty (label is floating)
  const shouldShowPlaceholder = isFocused && !preferredValue;

  const SOURCE_LABEL_MAP: Record<string, string> = {
    [DISPLAY_NAME_SOURCE.USER]: 'LGM Product Name',
    [DISPLAY_NAME_SOURCE.ORANGE_BOOK]: 'FDA Orange Book',
    [DISPLAY_NAME_SOURCE.CAS]: 'CAS Registry',
  };

  const CASCADE_TOOLTIP =
    'Product Name is resolved in order: LGM Product Name → FDA Orange Book ingredient → CAS Registry name.';

  // Synonyms-only path — used by the standalone synonyms_list field
  if (synonymsOnly) {
    return (
      <div style={componentStyles.synonymsSection}>
        <div style={componentStyles.synonymsLabel}>
          Synonyms
          {!isDisabled && tooltipText && (
            <>
              <span
                data-tooltip-id="synonyms-preferred-name-tooltip"
                data-tooltip-content={tooltipText}
                data-tooltip-place="top"
                style={componentStyles.tooltipIcon}>
                <Info size={16} />
              </span>
              <Tooltip
                style={componentStyles.tooltip}
                id="synonyms-preferred-name-tooltip"
              />
            </>
          )}
        </div>
        {sortedSynonyms.length === 0 ? (
          <div style={componentStyles.noSynonyms}>No synonyms available</div>
        ) : (
          <div style={componentStyles.synonymsContainer}>
            {sortedSynonyms.map((synonym, index) => {
              const isSelected = preferredValue === synonym;
              return isDisabled ? (
                <div key={index} style={componentStyles.synonymRow}>
                  <span style={componentStyles.synonymText}>
                    {renderSafeHtml(synonym)}
                  </span>
                </div>
              ) : (
                <div
                  key={index}
                  style={{
                    ...componentStyles.synonymRow,
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                  }}
                  onClick={() => handleRadioClick(synonym)}>
                  <input
                    type="radio"
                    checked={isSelected}
                    onChange={() => {}}
                    style={{ marginRight: '8px', cursor: 'pointer' }}
                  />
                  <span style={componentStyles.synonymText}>
                    {renderSafeHtml(synonym)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // VIEW mode: show the resolved display name prominently
  if (isDisabled) {
    const sourceText =
      displayNameSource != null
        ? (SOURCE_LABEL_MAP[displayNameSource] ?? displayNameSource)
        : null;

    return (
      <div style={componentStyles.viewBlock}>
        <div style={componentStyles.viewLabel}>
          {label}
          {sourceText && (
            <span style={componentStyles.sourceSubLabel}>
              <span style={componentStyles.sourceSubLabelPrefix}>
                Sourced from:
              </span>{' '}
              {sourceText}
            </span>
          )}
          <MuiTooltip
            title={CASCADE_TOOLTIP}
            placement="right"
            arrow
            slotProps={{ popper: { style: { zIndex: 10001 } } }}>
            <span
              style={componentStyles.cascadeHint}
              aria-label="How is Product Name resolved?">
              <Info size={13} />
            </span>
          </MuiTooltip>
        </div>
        <div style={componentStyles.viewValue}>
          {displayName ? (
            renderSafeHtml(displayName)
          ) : (
            <em style={componentStyles.missing}>Not set</em>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <p style={componentStyles.editHint}>
        Sets the LGM product name shown across the application. If left blank,
        the name is derived in order: Orange Book ingredient → CAS Registry
        name.
      </p>
      {/* Preferred Product Name Text Input */}
      <div style={styles.floatingFieldContainer}>
        <input
          id="preferred_product_name"
          type="text"
          value={preferredValue || ''}
          onChange={e => handlePreferredNameChange(e.target.value)}
          disabled={isDisabled}
          onFocus={onFocus}
          onBlur={onBlur}
          style={
            getFloatingInputStyle
              ? getFloatingInputStyle(false, 'preferred_product_name')
              : {}
          }
          placeholder={
            shouldShowPlaceholder
              ? 'Type a name, or select from synonyms below'
              : ' '
          }
        />
        <label
          htmlFor="preferred_product_name"
          style={
            getFloatingLabelStyle
              ? getFloatingLabelStyle(
                  'preferred_product_name',
                  hasValue || !!preferredValue
                )
              : {}
          }>
          {label}
        </label>
      </div>
    </>
  );
};

const componentStyles: Record<string, React.CSSProperties> = {
  viewBlock: {
    marginBottom: '16px',
  },
  viewLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#6b7280',
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  viewValue: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#111827',
    lineHeight: '1.4',
    wordBreak: 'break-word',
  },
  missing: {
    color: '#9ca3af',
    fontWeight: 'normal',
  },
  sourceSubLabel: {
    fontSize: '11px',
    color: '#6b7280',
    textTransform: 'none' as const,
    letterSpacing: 'normal',
    fontWeight: '400',
  },
  sourceSubLabelPrefix: {
    fontWeight: '600',
    color: '#6b7280',
  },
  cascadeHint: {
    display: 'inline-flex',
    alignItems: 'center',
    color: '#9ca3af',
    cursor: 'help',
  },
  synonymsSection: {
    marginBottom: '16px',
  },
  tooltipIcon: {
    marginLeft: '8px',
    cursor: 'pointer',
    position: 'relative',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  tooltip: {
    zIndex: 20,
  },
  synonymsLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px',
  },
  noSynonyms: {
    fontSize: '13px',
    color: '#9ca3af',
    fontStyle: 'italic',
    padding: '4px 0',
  },
  synonymsContainer: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    padding: '8px',
    backgroundColor: '#f9fafb',
  },
  synonymRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 4px',
    borderRadius: '3px',
  },
  synonymText: {
    fontSize: '14px',
    color: '#374151',
    wordBreak: 'break-word',
    flex: 1,
  },
  editHint: {
    margin: '0 0 12px',
    fontSize: '13px',
    color: '#6b7280',
    lineHeight: '1.5',
  },
};

export default SynonymsRadioField;
